import React, { useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "../../../store";
import {
  SelectChangeEvent,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Checkbox,
  DialogContentText,
  Box as MuiBox,
} from "@mui/material";
import Select, { SelectProps } from "../../../components/Select";
import SelectRationg, {
  SelectRatingProps,
} from "../../../components/SelectRating";
import Search, { SearchProps } from "../../../components/Search";
import { TextField, Autocomplete } from "@mui/material";
import {
  Container,
  SecondContainer,
  SecondBox,
  ThirdBox,
  Box,
  Typography,
} from "../styles";
import { getLanguageCode, defaultAllCode } from "../LanguageCode";
import DateRangePicker from "../../../components/DateRangePicker";
import { defaultAllCategory, getCategoryCode } from "../CategoryCode";
import {
  defaultAllRejectCategory,
  rejectCategoryLabel,
  rejectCategoryList,
} from "../RejectCategoryCode";
import {
  defaultAllExplicitClass,
  explicitClassLabel,
  explicitClassList,
  sexualRejectCategory,
} from "../ExplicitClassCode";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import dayjs from "dayjs";
import { useCookies, Cookies } from "react-cookie";
import moment from "moment";
import GradeIcon from "@mui/icons-material/Grade";

/**
 * 필터 요약 줄의 "라벨 + 값" 한 쌍.
 *
 * 라벨과 값을 하나의 요소로 묶는 게 핵심이다. 예전처럼 형제 <p> 로 나열하면
 * 요약 줄에 걸린 flex-wrap 이 라벨과 값 사이에서 줄을 끊어, 라벨만 윗줄에
 * 남고 값이 다음 줄로 넘어가는 일이 생겼다.
 *
 * 쌍 하나가 한 덩어리로 흐르므로 항목마다 폭(% / px)을 재서 맞출 필요가 없다.
 * 폭 지정을 없앤 자리는 항목 사이 여백(mr)이 대신한다.
 */
const FilterItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <MuiBox
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      mr: "16px",
      whiteSpace: "nowrap",
    }}
  >
    <Typography sx={{ color: "#79747E" }}>{label}</Typography>
    {/* 값 자리를 inline-flex 로 둬서 별 아이콘이 그대로 자식으로 들어간다.
        예전에 <p> 안에 display:flex <span> 을 한 겹 더 두던 걸 없앤다. */}
    <Typography sx={{ display: "inline-flex", alignItems: "center" }}>
      {children}
    </Typography>
  </MuiBox>
);

/** 점수 필터(Mixability / Reference / Asset / Entertainment)의 별 아이콘. */
const ratingStars = (count: number) =>
  Array.from({ length: count }, (_, index) => (
    <GradeIcon key={index} fontSize="small" sx={{ color: "#faaf00" }} />
  ));

const Handler = () => {
  const {
    TemplatesStore: {
      gridOption: { page, rowsPerPage },
      application,
      setApplication,
      status,
      setStatus,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      searchText,
      setSearchText,
      searchOption,
      setSearchOption,
      setLanguageChange,
      language,
      searchHandler,
      category,
      setCategoryChange,
      mixability,
      setMixability,
      reference,
      setReference,
      asset,
      setAsset,
      entertainment,
      setEntertainment,
      setRows,
      ratio,
      setRatio,
      deleted,
      setDeleted,
      rejectCategory,
      setRejectCategory,
      explicitClass,
      setExplicitClass,
    },
  } = useStore();

  const [btnOpen, setBtnOpen] = useState(false);
  const [firstReviewerStatus, setFirstReviewerStatus] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies();
  const expires = moment().add("100", "y").toDate();
  const gGookies = new Cookies();
  const getCookie = (item: string) => {
    return gGookies.get(item);
  };

  const selectProps: SelectProps = {
    width: 122,
    name: "Status",
    value: status,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setStatus(event.target.value);
    },
    defaultValue: "All",
    list: [
      { label: "Preparing", value: "Preparing" },
      { label: "Rejected", value: "Rejected" },
      { label: "Published", value: "Published" },
      { label: "Blind", value: "Blind" },
      { label: "All", value: "All" },
    ],
  };

  // Rejected 조회일 때만 쓰는 필터라 그 외 상태에서는 비활성화한다.
  const isRejectCategoryEnabled = status === "Rejected";

  const selectRejectCategoryProps: SelectProps = {
    width: 175,
    name: "Reject Category",
    value: rejectCategory,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setRejectCategory(event.target.value);
    },
    defaultValue: defaultAllRejectCategory,
    list: rejectCategoryList,
    disabled: !isRejectCategoryEnabled,
  };

  // SexualOrViolent 반려에만 붙는 세부 분류라, 그 카테고리를 고른 경우에만 켠다.
  // 비활성 상태를 그대로 두는 것 자체가 두 필터의 관계를 보여주는 설명이 된다.
  const isExplicitClassEnabled =
    isRejectCategoryEnabled && rejectCategory === sexualRejectCategory;

  const selectExplicitClassProps: SelectProps = {
    width: 205,
    name: "Sexual Content Class",
    value: explicitClass,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setExplicitClass(event.target.value);
    },
    defaultValue: defaultAllExplicitClass,
    list: explicitClassList,
    disabled: !isExplicitClassEnabled,
  };

  const selectApplicationProps: SelectProps = {
    width: 122,
    name: "Application",
    value: application,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setApplication(event.target.value);
    },
    defaultValue: "all",
    list: [
      { label: "All", value: "all" },
      { label: "KineMaster", value: "kinemaster" },
      { label: "Spring", value: "spring" },
    ],
  };

  const selectRatioProps: SelectProps = {
    width: 175,
    name: "Ratio",
    value: ratio,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setRatio(event.target.value);
    },
    defaultValue: "all",
    list: [
      { label: "All", value: "all" },
      { label: "16:9", value: "16:9" },
      { label: "9:16", value: "9:16" },
      { label: "1:1", value: "1:1" },
      { label: "4:3", value: "4:3" },
      { label: "3:4", value: "3:4" },
      { label: "4:5", value: "4:5" },
      { label: "2.35:1", value: "2.35:1" },
    ],
  };

  const selectDeletedProps: SelectProps = {
    width: 140,
    name: "Deleted",
    value: deleted,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setDeleted(event.target.value);
    },
    defaultValue: "include",
    list: [
      { label: "Include", value: "include" },
      { label: "Exclude", value: "exclude" },
      { label: "Only", value: "only" },
    ],
  };

  const selectMixabilityProps: SelectRatingProps = {
    width: 155,
    name: "Mixability",
    value: mixability,
    defaultValue: 1,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setMixability(event.target.value);
    },
  };

  const selectReferenceProps: SelectRatingProps = {
    width: 155,
    name: "Reference",
    value: reference,
    defaultValue: 1,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setReference(event.target.value);
    },
  };

  const selectAssetProps: SelectRatingProps = {
    width: 155,
    name: "Asset",
    value: asset,
    defaultValue: 1,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setAsset(event.target.value);
    },
  };

  const selectEntertainmentProps: SelectRatingProps = {
    width: 155,
    name: "Entertainment",
    value: entertainment,
    defaultValue: 1,
    selectHandler: (event: SelectChangeEvent<any>) => {
      setEntertainment(event.target.value);
    },
  };

  const rangeDateProps = {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
  };
  const searchProps: SearchProps = {
    option: {
      value: searchOption,
      selectHandler: (event: SelectChangeEvent<any>) => {
        setSearchOption(event.target.value);
        setSearchText("");
      },
      defaultValue: "templateId",
      list: [
        { label: "Template ID", value: "templateId" },
        { label: "Description", value: "description" },
        { label: "User ID", value: "userId" },
        { label: "User Name", value: "username" },
        // { label: "User Nickname", value: "name" },
        { label: "Reviewer", value: "reviewer" },
        { label: "First Reviewer", value: "firstReviewer" },
        { label: "Rejected Reason", value: "rejectedReason" },
        { label: "Extra Description", value: "extraDescription" },
        // { label: "Score", value: "score" },
        { label: "Dynamic Link", value: "dynamicLink" },
      ],
    },
    value: searchText,
    textHandler: (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    removeHandler: () => {
      setSearchText("");
    },
    action: () => {
      searchHandler();
    },
    keyHandler: (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") searchHandler();
    },
  };
  const onChangeLang = (event: any, value: any) => {
    if (value) {
      // setAutoLangCode(value);

      setLanguageChange(value);
    }
  };

  const onChangeCategory = (event: any, value: any) => {
    if (value) {
      setCategoryChange(value);
    }
  };

  const handleButton = () => {
    setCookie("page", page, { path: "/", expires });
    setCookie("rowsPerPage", rowsPerPage, { path: "/", expires });
    setCookie("startDate", startDate, { path: "/", expires });
    setCookie("endDate", endDate, { path: "/", expires });
    setCookie("application", application, { path: "/", expires });
    setCookie("status", status, { path: "/", expires });
    setCookie("language", JSON.stringify(language), { path: "/", expires });
    setCookie("category", JSON.stringify(category), { path: "/", expires });
    setCookie("mixability", mixability, { path: "/", expires });
    setCookie("reference", reference, { path: "/", expires });
    setCookie("asset", asset, { path: "/", expires });
    setCookie("entertainment", entertainment, { path: "/", expires });
    setCookie("ratio", JSON.stringify(ratio), { path: "/", expires });
    setCookie("deleted", deleted, { path: "/", expires });
    setCookie("rejectCategory", rejectCategory, { path: "/", expires });
    setCookie("explicitClass", explicitClass, { path: "/", expires });

    setRows();
    setBtnOpen(false);
  };

  const handleClose = () => {
    setApplication(getCookie("application") ? getCookie("application") : "all");
    setStatus(getCookie("status") ? getCookie("status") : "All");

    setStartDate(
      window.location.pathname.includes("userid")
        ? dayjs("2023/01/01", "yyyy/MM/dd")
        : getCookie("startDate")
        ? getCookie("startDate")
        : dayjs().subtract(1, "week").toDate()
    );
    setEndDate(
      window.location.pathname.includes("userid")
        ? dayjs().toDate()
        : getCookie("endDate")
        ? dayjs(getCookie("endDate"), "yyyy/MM/dd").toDate()
        : dayjs().toDate()
    );

    setLanguageChange(
      getCookie("language") ? getCookie("language") : defaultAllCode
    );
    setCategoryChange(
      getCookie("category") ? getCookie("category") : defaultAllCategory
    );

    setMixability(
      getCookie("mixability")
        ? getCookie("mixability") === "0"
          ? 1
          : getCookie("mixability")
        : 1
    );
    setReference(
      getCookie("reference")
        ? getCookie("reference") === "0"
          ? 1
          : getCookie("reference")
        : 1
    );
    setAsset(
      getCookie("asset")
        ? getCookie("asset") === "0"
          ? 1
          : getCookie("asset")
        : 1
    );
    setEntertainment(
      getCookie("entertainment")
        ? getCookie("entertainment") === "0"
          ? 1
          : getCookie("entertainment")
        : 1
    );

    setRatio(getCookie("ratio") ? getCookie("ratio") : "all");
    setDeleted(getCookie("deleted") ? getCookie("deleted") : "include");
    // setStatus 가 Rejected 아닌 상태로 되돌리면 rejectCategory 도 함께 초기화하므로,
    // 복원한 status 가 Rejected 인 경우에만 쿠키값을 다시 적용한다.
    const restoredStatus = getCookie("status") ? getCookie("status") : "All";
    const restoredRejectCategory =
      restoredStatus === "Rejected" && getCookie("rejectCategory")
        ? getCookie("rejectCategory")
        : defaultAllRejectCategory;
    setRejectCategory(restoredRejectCategory);
    // setRejectCategory 가 SexualOrViolent 아닌 값으로 explicitClass 를 초기화하므로,
    // 복원한 카테고리가 SexualOrViolent 인 경우에만 쿠키값을 다시 적용한다.
    setExplicitClass(
      restoredRejectCategory === sexualRejectCategory &&
        getCookie("explicitClass")
        ? getCookie("explicitClass")
        : defaultAllExplicitClass
    );

    setBtnOpen(false);
  };

  /**
   * 필터 다이얼로그 안에서 Enter 를 누르면 OK(handleButton) 와 동일하게 동작시킨다.
   *
   * bubble 이 아니라 capture 단계에서 처리한다. bubble 로 두면 필터 값을 바꾼 뒤
   * Select 에 포커스가 남아 있을 때 MUI Select 가 Enter 를 먼저 가로채 메뉴를 다시
   * 열고 preventDefault 해버려서 검색이 실행되지 않는다. capture 에서 먼저 잡고
   * stopPropagation 으로 그 동작을 막는다.
   *
   * 단, 아래 경우는 Enter 가 다른 의미를 가지므로 그대로 흘려보낸다.
   *  - IME(한글 등) 조합 확정용 Enter
   *  - 드롭다운(Select 메뉴 / Autocomplete 목록)이 열려 있을 때 → 항목 선택
   *  - 버튼에 포커스가 있을 때 → 그 버튼의 클릭 (Cancel / OK / No / Yes)
   *  - 달력의 날짜 직접 입력 칸 → 입력값 확정
   *  - 중첩 확인 다이얼로그가 열려 있을 때
   */
  const handleDialogKeyDownCapture = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    if (event.nativeEvent.isComposing) return;

    const target = event.target as HTMLElement;
    if (document.querySelector(".MuiPopover-root, .MuiAutocomplete-popper")) {
      return;
    }
    if (target.closest("button")) return;
    if (target.closest(".rdrDateDisplayWrapper")) return;
    if (firstReviewerStatus) return;

    event.preventDefault();
    event.stopPropagation();
    handleButton();
  };

  const handleFristReviewer = () => {
    setStatus("Published");
    setSearchOption("firstReviewer");
    setSearchText("auto.system@kinemaster.com");
    setRows();
    setBtnOpen(false);
    setFirstReviewerStatus(false);
  };

  return (
    <>
      <SecondContainer>
        <FilterAltIcon
          onClick={() => {
            setBtnOpen(true);
          }}
          sx={{ cursor: "pointer", color: "#6750a4" }}
        />
        <SecondBox
          sx={{
            display: "flex",
            flexDirection: "column",
            // width:100% 로 두면 space-between 컨테이너에서 남은 폭을 전부 먹어
            // Search 를 밀어낸다. flex 로 남은 공간만 차지하게 한다.
            flex: 1,
            minWidth: 0,
          }}
        >
          <ThirdBox
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              flexWrap: "wrap",
              pt: "10px",
            }}
          >
            <FilterItem label="Application">
              {application === "kinemaster"
                ? "KineMaster"
                : application === "spring"
                ? "Spring"
                : "All"}
            </FilterItem>
            <FilterItem label="Status">{status}</FilterItem>
            <FilterItem label="Date">
              {dayjs(startDate).format("YYYY-MM-DD") +
                " ~ " +
                dayjs(endDate).format("YYYY-MM-DD")}
            </FilterItem>
            <FilterItem label="Language">{language.name}</FilterItem>
            <FilterItem label="Category">{category.key}</FilterItem>
            <FilterItem label="Deleted">
              {deleted === "exclude"
                ? "Exclude"
                : deleted === "only"
                ? "Only"
                : "Include"}
            </FilterItem>
            {/* Rejected 조회에서만 의미가 있는 값이라 그 외에는 줄에 노출하지 않는다. */}
            {isRejectCategoryEnabled && (
              <FilterItem label="Reject Category">
                {rejectCategory === defaultAllRejectCategory
                  ? "All"
                  : rejectCategoryLabel(rejectCategory)}
              </FilterItem>
            )}
            {/* Reject Category 와 같은 이유로, 의미가 있는 조합에서만 줄에 노출한다. */}
            {isExplicitClassEnabled && (
              <FilterItem label="Sexual Content Class">
                {explicitClass === defaultAllExplicitClass
                  ? "All"
                  : explicitClassLabel(explicitClass)}
              </FilterItem>
            )}
          </ThirdBox>
          <ThirdBox
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              flexWrap: "wrap",
              pt: "5px",
            }}
          >
            <FilterItem label="Mixability">{ratingStars(mixability)}</FilterItem>
            <FilterItem label="Reference">{ratingStars(reference)}</FilterItem>
            <FilterItem label="Asset">{ratingStars(asset)}</FilterItem>
            <FilterItem label="Entertainment">
              {ratingStars(entertainment)}
            </FilterItem>
            <FilterItem label="Ratio">
              {ratio === "all" ? "All" : ratio}
            </FilterItem>
          </ThirdBox>
        </SecondBox>

        <SecondBox>
          <Search {...searchProps} />
        </SecondBox>
      </SecondContainer>

      <Dialog
        open={btnOpen}
        onClose={handleClose}
        onKeyDownCapture={handleDialogKeyDownCapture}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "900px",
            minWidth: "900px",
            maxHeight: "650px",
            minHeight: "650px",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Select {...selectApplicationProps} />
            <Select {...selectProps} />
            <Autocomplete
              disablePortal
              size="small"
              id="combo-box-demo"
              value={language}
              options={[defaultAllCode].concat(getLanguageCode)}
              onChange={onChangeLang}
              getOptionLabel={(option: any) => {
                if (option.name === "All") {
                  return `All`;
                } else {
                  return `${option.name} (${option.value})`;
                }
              }}
              renderInput={(params) => {
                return <TextField {...params} label="Language" />;
              }}
              isOptionEqualToValue={(option: any, value: any) =>
                option.name === value.name && option.value === value.value
              }
              sx={{
                "& .MuiFormControl-root.MuiTextField-root": {
                  width: 240,
                  mr: "4px",
                },
              }}
            />
            <Autocomplete
              disablePortal
              size="small"
              id="combo-box-demo"
              value={category}
              options={[defaultAllCategory].concat(getCategoryCode)}
              onChange={onChangeCategory}
              getOptionLabel={(option: any) => {
                if (option.key === "All") {
                  return `All`;
                } else {
                  return `${option.key}`;
                }
              }}
              renderInput={(params) => {
                return <TextField {...params} label="Category" />;
              }}
              isOptionEqualToValue={(option: any, value: any) =>
                option.key === value.key
              }
              sx={{
                "& .MuiFormControl-root.MuiTextField-root": {
                  width: 150,
                  mr: "4px",
                },
              }}
            />{" "}
            <FormControlLabel
              control={
                <Checkbox
                  checked={firstReviewerStatus}
                  onChange={(e) => setFirstReviewerStatus(e.target.checked)}
                />
              }
              label="Auto Publishing"
              sx={{ mr: 0 }}
            />
          </Box>

          <Box sx={{ pt: "10px", pb: "10px" }}>
            <SelectRationg {...selectMixabilityProps} />
            <SelectRationg {...selectReferenceProps} />
            <SelectRationg {...selectAssetProps} />
            <SelectRationg {...selectEntertainmentProps} />
            <Select {...selectRatioProps} />
          </Box>
          <Box sx={{ pb: "10px" }}>
            <Select {...selectDeletedProps} />
            <Select {...selectRejectCategoryProps} />
            <Select {...selectExplicitClassProps} />
          </Box>
          <DialogContent
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              pl: 0,
              pt: 1,
            }}
          >
            <DateRangePicker {...rangeDateProps} />
          </DialogContent>
        </DialogContent>
        <DialogActions style={{ justifyContent: "center" }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleButton}>OK</Button>
        </DialogActions>

        <Dialog open={firstReviewerStatus} PaperProps={{ id: "openedDlg" }}>
          <DialogContent>
            <DialogContentText>
              Search is possible only in Publish status.
              <br /> Would you like to search it?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <Button onClick={() => setFirstReviewerStatus(false)}>No</Button>
              <Button onClick={handleFristReviewer}>Yes</Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Dialog>
    </>
  );
};

export default observer(Handler);
